import React, { useState } from "react";
import { Button } from "../../../../ui/components/Button";
import { TextField } from "../../../../ui/components/TextField";
import { FeatherEdit } from "@subframe/core";
import { FeatherSave } from "@subframe/core";
import { FeatherX } from "@subframe/core";
import ReviewChangesModal, { ChangeItem } from "./ReviewChangesModal";
import { ReviewPendingInlineValue } from "./ReviewFieldDiffHint";

export type DatesFieldDiffSlice = { oldText: string; newText: string };

interface DatesData {
  documentDate: string;
  dueDate: string;
}

interface DatesEditableFieldProps {
  initialData: DatesData;
  onSave?: (newData: DatesData) => void;
  disabled?: boolean;
  /** FBAC: maps JSON keys date / due_date to before→after display strings */
  fieldDiffs?: Partial<Record<"date" | "due_date", DatesFieldDiffSlice>>;
  pendingReviewDiffActive?: boolean;
}

const DatesEditableField: React.FC<DatesEditableFieldProps> = ({
  initialData = {
    documentDate: "March 15, 2024",
    dueDate: "April 14, 2024",
  },
  onSave,
  disabled = false,
  fieldDiffs,
  pendingReviewDiffActive = false,
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [data, setData] = useState<DatesData>(initialData);
  const [editData, setEditData] = useState<DatesData>(initialData);
  const [isReviewModalOpen, setIsReviewModalOpen] = useState(false);
  const [pendingChanges, setPendingChanges] = useState<ChangeItem[]>([]);

  const handleEdit = () => {
    setEditData(data);
    setIsEditing(true);
  };

  const handleSave = () => {
    // Check for changes and create change items
    const changes: ChangeItem[] = [];

    Object.keys(editData).forEach((key) => {
      const fieldKey = key as keyof DatesData;
      if (data[fieldKey] !== editData[fieldKey]) {
        changes.push({
          field:
            key.charAt(0).toUpperCase() +
            key.slice(1).replace(/([A-Z])/g, " $1"),
          previousValue: data[fieldKey],
          newValue: editData[fieldKey],
        });
      }
    });

    if (changes.length > 0) {
      setPendingChanges(changes);
      setIsReviewModalOpen(true);
    } else {
      setIsEditing(false);
    }
  };

  const handleCancel = () => {
    setEditData(data);
    setIsEditing(false);
  };

  // Helper function to convert human-readable date to yyyy-mm-dd format
  const convertToISODate = (dateString: string): string => {
    try {
      // Try to parse the date string
      const date = new Date(dateString);

      // Check if the date is valid
      if (isNaN(date.getTime())) {
        console.warn(`Invalid date: ${dateString}`);
        return dateString; // Return original if parsing fails
      }

      // Format as yyyy-mm-dd
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const day = String(date.getDate()).padStart(2, "0");

      return `${year}-${month}-${day}`;
    } catch (error) {
      console.error(`Error converting date: ${dateString}`, error);
      return dateString; // Return original if error occurs
    }
  };

  const handleConfirmChanges = () => {
    setData(editData);
    setIsEditing(false);
    setIsReviewModalOpen(false);
    setPendingChanges([]);

    // Convert dates to yyyy-mm-dd format before saving
    const dataToSave: DatesData = {
      documentDate: convertToISODate(editData.documentDate),
      dueDate: convertToISODate(editData.dueDate),
    };

    // Optional callback to parent component this will update the json !! to be used to send to DB later
    onSave?.(dataToSave);
  };

  const handleCancelChanges = () => {
    setIsReviewModalOpen(false);
    setPendingChanges([]);
    // Keep editing mode open so user can continue editing
  };

  const handleFieldChange = (field: keyof DatesData, value: string) => {
    setEditData((prev) => ({ ...prev, [field]: value }));
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      handleSave();
    } else if (e.key === "Escape") {
      handleCancel();
    }
  };

  return (
    <div className="flex w-full flex-col items-start gap-3 border-b border-solid border-neutral-100 pb-4">
      <div className="flex w-full items-center justify-between">
        <span className="text-body-bold font-body-bold text-subtext-color">
          Dates
        </span>
        {isEditing ? (
          <div className="flex items-center gap-2">
            <Button variant="brand-primary" size="small" onClick={handleSave}>
              <FeatherSave />
            </Button>
            <Button
              variant="neutral-tertiary"
              size="small"
              onClick={handleCancel}
            >
              <FeatherX />
            </Button>
          </div>
        ) : (
          !disabled && (
            <FeatherEdit
              className="text-body-bold font-body-bold text-subtext-color cursor-pointer hover:text-brand-600 transition-colors"
              onClick={handleEdit}
            />
          )
        )}
      </div>
      <div className="w-full items-start gap-4 grid grid-cols-2">
        <div className="flex grow shrink-0 basis-0 flex-col items-start gap-1">
          <span className="text-body font-body text-subtext-color">
            Document Date
          </span>
          {isEditing ? (
            <TextField label="" helpText="">
              <TextField.Input
                placeholder="March 15, 2024"
                value={editData.documentDate}
                onChange={(e) =>
                  handleFieldChange("documentDate", e.target.value)
                }
                onKeyDown={handleKeyPress}
                autoFocus
              />
            </TextField>
          ) : (
            <ReviewPendingInlineValue
              pendingReviewDiffActive={pendingReviewDiffActive}
              isEditing={isEditing}
              oldText={fieldDiffs?.date?.oldText ?? ""}
              newText={fieldDiffs?.date?.newText ?? ""}
            >
              <span className="text-body-bold font-body-bold text-default-font">
                {data.documentDate}
              </span>
            </ReviewPendingInlineValue>
          )}
        </div>
        <div className="flex grow shrink-0 basis-0 flex-col items-start gap-1">
          <span className="text-body font-body text-subtext-color">
            Due Date
          </span>
          {isEditing ? (
            <TextField label="" helpText="">
              <TextField.Input
                placeholder="April 14, 2024"
                value={editData.dueDate}
                onChange={(e) => handleFieldChange("dueDate", e.target.value)}
                onKeyDown={handleKeyPress}
              />
            </TextField>
          ) : (
            <ReviewPendingInlineValue
              pendingReviewDiffActive={pendingReviewDiffActive}
              isEditing={isEditing}
              oldText={fieldDiffs?.due_date?.oldText ?? ""}
              newText={fieldDiffs?.due_date?.newText ?? ""}
            >
              <span className="text-body-bold font-body-bold text-default-font">
                {data.dueDate}
              </span>
            </ReviewPendingInlineValue>
          )}
        </div>
      </div>

      {/* Review Changes Modal */}
      <ReviewChangesModal
        open={isReviewModalOpen}
        onOpenChange={setIsReviewModalOpen}
        changes={pendingChanges}
        onConfirm={handleConfirmChanges}
        onCancel={handleCancelChanges}
        title="Review Dates Changes"
        description="These changes will affect the invoice calculations and tax implications. Please review carefully before proceeding."
      />
    </div>
  );
};

export default DatesEditableField;
